<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useSectorsStore } from 'dashboard/stores/sectors';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const emit = defineEmits(['selectSector']);

const { t } = useI18n();
const store = useSectorsStore();
const sectors = computed(() => store.records);
const uiFlags = computed(() => store.uiFlags);

const showFormModal = ref(false);
const showDeleteModal = ref(false);
const selectedSector = ref(null);
const activeSectorId = ref(null);

const form = reactive({ name: '', description: '', active: true });
const isFormInvalid = computed(() => !form.name.trim());

const resetForm = () => {
  form.name = '';
  form.description = '';
  form.active = true;
};

const openAdd = () => {
  selectedSector.value = null;
  resetForm();
  showFormModal.value = true;
};

const openEdit = sector => {
  selectedSector.value = sector;
  form.name = sector.name;
  form.description = sector.description || '';
  form.active = sector.active;
  showFormModal.value = true;
};

const closeFormModal = () => {
  showFormModal.value = false;
};

const submitForm = async () => {
  if (isFormInvalid.value) return;
  try {
    if (selectedSector.value) {
      await store.update({ id: selectedSector.value.id, ...form });
    } else {
      await store.create({ ...form });
    }
    useAlert(t('PRODUCTS_MGMT.SECTORS.SAVED'));
    closeFormModal();
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_MGMT.SECTORS.SAVE_ERROR'));
  }
};

const openDelete = sector => {
  selectedSector.value = sector;
  showDeleteModal.value = true;
};

const confirmDelete = async () => {
  try {
    await store.delete(selectedSector.value.id);
    useAlert(t('PRODUCTS_MGMT.SECTORS.DELETED'));
  } catch (error) {
    useAlert(error?.message || t('PRODUCTS_MGMT.SECTORS.DELETE_ERROR'));
  } finally {
    showDeleteModal.value = false;
  }
};

const selectSector = sector => {
  activeSectorId.value = sector.id;
  emit('selectSector', sector);
};

onMounted(() => store.get());

defineExpose({ sectors });
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1"
  >
    <div class="flex items-center justify-between">
      <h3 class="text-base font-medium text-n-slate-12">
        {{ t('PRODUCTS_MGMT.SECTORS.TITLE') }}
      </h3>
      <Button
        :label="t('PRODUCTS_MGMT.SECTORS.ADD')"
        size="sm"
        @click="openAdd"
      />
    </div>
    <BaseTable
      :headers="[t('PRODUCTS_MGMT.SECTORS.TITLE'), t('COMMON_MGMT.ACTIVE'), '']"
      :items="sectors"
      :no-data-message="t('PRODUCTS_MGMT.SECTORS.NO_DATA')"
    >
      <template #row="{ items }">
        <BaseTableRow
          v-for="sector in items"
          :key="sector.id"
          :item="sector"
          class="cursor-pointer"
          :class="{ 'bg-n-blue-2': activeSectorId === sector.id }"
          @click="selectSector(sector)"
        >
          <template #default>
            <BaseTableCell>
              <span class="text-body-main text-n-slate-12">{{
                sector.name
              }}</span>
            </BaseTableCell>
            <BaseTableCell>
              <span
                class="px-2 py-0.5 text-xs rounded-full"
                :class="
                  sector.active
                    ? 'bg-n-teal-3 text-n-teal-11'
                    : 'bg-n-slate-3 text-n-slate-11'
                "
              >
                {{
                  sector.active
                    ? t('COMMON_MGMT.ACTIVE')
                    : t('COMMON_MGMT.INACTIVE')
                }}
              </span>
            </BaseTableCell>
            <BaseTableCell align="end">
              <div class="flex justify-end gap-2" @click.stop>
                <Button
                  icon="i-woot-edit-pen"
                  slate
                  sm
                  @click="openEdit(sector)"
                />
                <Button
                  icon="i-woot-bin"
                  slate
                  sm
                  @click="openDelete(sector)"
                />
              </div>
            </BaseTableCell>
          </template>
        </BaseTableRow>
      </template>
    </BaseTable>

    <woot-modal v-model:show="showFormModal" :on-close="closeFormModal">
      <div class="flex flex-col w-full gap-4 p-6">
        <h3 class="text-lg font-medium text-n-slate-12">
          {{
            selectedSector
              ? t('PRODUCTS_MGMT.SECTORS.EDIT')
              : t('PRODUCTS_MGMT.SECTORS.ADD')
          }}
        </h3>
        <Input
          v-model="form.name"
          :placeholder="t('PRODUCTS_MGMT.SECTORS.NAME_PLACEHOLDER')"
          autofocus
        />
        <TextArea
          v-model="form.description"
          :placeholder="t('PRODUCTS_MGMT.SECTORS.DESCRIPTION_PLACEHOLDER')"
        />
        <div class="flex items-center gap-2">
          <Switch v-model="form.active" />
          <span class="text-sm text-n-slate-11">{{
            t('COMMON_MGMT.ACTIVE')
          }}</span>
        </div>
        <div class="flex justify-end gap-2">
          <Button
            :label="t('COMMON_MGMT.CANCEL')"
            variant="link"
            @click="closeFormModal"
          />
          <Button
            :label="t('COMMON_MGMT.SAVE')"
            :disabled="isFormInvalid"
            :is-loading="uiFlags.creatingItem || uiFlags.updatingItem"
            @click="submitForm"
          />
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="() => (showDeleteModal = false)"
      :on-confirm="confirmDelete"
      :title="t('PRODUCTS_MGMT.SECTORS.DELETE_TITLE')"
      :message="t('PRODUCTS_MGMT.SECTORS.DELETE_MESSAGE')"
      :message-value="selectedSector?.name"
      :confirm-text="t('COMMON_MGMT.DELETE')"
      :reject-text="t('COMMON_MGMT.CANCEL')"
    />
  </div>
</template>
